<template>
  <v-menu
    v-model="menu"
    offset-y
    nudge-bottom="0"
    nudge-left="150"
    :close-on-content-click="false"
  >
    <template v-slot:activator="menuActivator">
      <v-tooltip top>
        <template v-slot:activator="tooltipActivator">
          <div
            v-on="tooltipActivator.on"
            class="date-picker d-flex align-center"
          >
            <v-icon
              class="cursor-pointer"
              v-on="menuActivator.on"
              size="12"
              :color="startDate || endDate ? 'blue' : 'blue-grey'"
            >
              mdi-calendar-month-outline
            </v-icon>
          </div>
        </template>
        {{ $t('common.edit_date') }}
      </v-tooltip>
    </template>
    <div class="white pa-4 date-picker-container">
      <div class="d-flex justify-between">
        <v-text-field
          dense
          hide-details
          outlined
          readonly
          class="mr-2"
          :class="{
            'active v-input--is-focused v-text-field--outlined primary--text': startDateIsFocused,
          }"
          :value="startDateLabel"
          @click="turnFocusOn(true)"
        >
          <template v-slot:label>
            <span class="caption input-label-active">{{
              $t('common.date.start_date')
            }}</span>
          </template>
        </v-text-field>
        <v-text-field
          dense
          hide-details
          outlined
          readonly
          class="ml-2"
          :class="{
            'active v-input--is-focused v-text-field--outlined primary--text': endDateIsFocused,
          }"
          :value="endDateLabel"
          @click="turnFocusOn()"
        >
          <template v-slot:label>
            <span class="caption input-label-active">{{
              $t('common.date.end_date')
            }}</span>
          </template>
        </v-text-field>
      </div>
      <v-date-picker
        v-if="startDateIsFocused"
        v-model="startDate"
        no-title
        :allowedDates="startDateAllowed"
        @input="handleStartDateChange"
      />
      <v-date-picker
        v-if="endDateIsFocused"
        v-model="endDate"
        no-title
        :allowedDates="endDateAllowed"
        @input="handleEndDateChange"
      />
      <v-divider />
      <div class="d-flex justify-between mt-3">
        <Button plain :text="$t('filters.clear')" @click="handleClear" />
        <Button plain :text="$t('common.button.close')" @click="menu = false" />
      </div>
    </div>
  </v-menu>
</template>

<script>
import { utils } from 'frontend-js-shared'
import Button from '@/components/UI/Button.vue'

export default {
  name: 'DateRangePicker',

  components: {
    Button,
  },

  props: {
    allowedDates: { type: Function },
    dates: { type: Array, default: () => [] },
  },

  data() {
    return {
      startDate: this.dates?.[0]
        ? utils.dates.formatDate(this.dates?.[0], 'YYYY-MM-DD')
        : null,
      endDate: this.dates?.[1]
        ? utils.dates.formatDate(this.dates?.[1], 'YYYY-MM-DD')
        : null,
      startDateIsFocused: true,
      endDateIsFocused: false,
      menu: false,
    }
  },

  watch: {
    dates() {
      if (!this.startDate && !this.endDate) {
        this.startDate = this.dates?.[0] ?? null
        this.endDate = this.dates?.[1] ?? null
      }
    },

    menu(isOpen, wasOpen) {
      if (isOpen || !wasOpen) return
      if (!this.startDate || !this.endDate) return

      this.$emit('change', [this.startDate, this.endDate])
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.startDate && !this.endDate && this.dates?.length === 0)
        this.menu = true
    })
  },

  computed: {
    startDateLabel() {
      return this.startDate
        ? utils.dates.formatDate(
            utils.dates.getStartOfDay(this.startDate),
            'MMM DD, YYYY'
          )
        : ''
    },

    endDateLabel() {
      return this.endDate
        ? utils.dates.formatDate(
            utils.dates.getStartOfDay(this.endDate),
            'MMM DD, YYYY'
          )
        : ''
    },
  },

  methods: {
    turnFocusOn(focusStart = false) {
      this.startDateIsFocused = focusStart
      this.endDateIsFocused = !focusStart
    },

    startDateAllowed(date) {
      const startDateIsMinorThanEnd = this.endDate
        ? date <=
          utils.dates.formatDate(
            utils.dates.getStartOfDay(this.endDate),
            'YYYY-MM-DD'
          )
        : true
      if (this.allowedDates) {
        return this.allowedDates(date) && startDateIsMinorThanEnd
      }
      return startDateIsMinorThanEnd
    },

    endDateAllowed(date) {
      const endDateIsMajorThanStart = this.startDate
        ? date >=
          utils.dates.formatDate(
            utils.dates.getStartOfDay(this.startDate),
            'YYYY-MM-DD'
          )
        : true
      if (this.allowedDates) {
        return this.allowedDates(date) && endDateIsMajorThanStart
      }
      return endDateIsMajorThanStart
    },

    handleStartDateChange() {
      if (!this.endDate) {
        this.turnFocusOn()
      }
    },

    handleEndDateChange() {
      if (!this.startDate) {
        this.turnFocusOn(true)
      }
    },

    handleClear() {
      this.startDate = null
      this.endDate = null
      this.turnFocusOn(true)
      this.$emit('change', [])
    },
  },
}
</script>

<style lang="scss">
.date-picker-container {
  max-width: min-content;
}
.date-picker-container
  div
  .v-input.active
  .v-input__control
  .v-input__slot
  fieldset
  legend {
  min-width: 20px !important;
  max-width: 150px !important;
}
.date-picker-container
  div
  .v-input.active
  .v-input__control
  .v-input__slot
  .v-text-field__slot
  .v-label {
  transform: translateY(-18px) scale(0.75);
  color: $color-primary;
  background: $color-white;
  padding-right: 4px;
  text-overflow: none;
  overflow: visible;
  min-width: min-content;
}
.date-picker-container
  div
  .v-input
  .v-input__control
  .v-input__slot
  .v-text-field__slot {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
}
</style>
